import type {
  IAttendanceSummaryUser,
  ICreateUserMonthlySalaryPayload,
  ISalaryPreviewError,
  ISalaryPreviewUser,
} from "@/interface/attendance.interface";
import type { IPageInfo } from "@/interface/common.interface";
import type { EmployeeCategory, IUser } from "@/interface/employeeOnBoard";
import type { ISalaryUserBackup } from "@/components/common/SalaryConfigTable";

interface QueryFilters {
  selectedBranchId: string;
  selectedSiteId: string;
  selectedCategory: EmployeeCategory;
  searchValue: string;
}

interface AttendanceQueryFilters extends QueryFilters {
  attendanceStartDate: string;
  attendanceEndDate: string;
  isSalaryCreated?: string | number;
}

interface PagedQueryOptions {
  currentPage: number;
  currentLimit: number;
}

export type SalarySubTab = "users" | "errors";

export const appendCommonEmployeeParams = (
  queryParams: URLSearchParams,
  filters: QueryFilters,
) => {
  if (filters.selectedBranchId) {
    queryParams.append("branchId", filters.selectedBranchId);
  }
  if (filters.selectedSiteId) {
    queryParams.append("siteId", filters.selectedSiteId);
  }
  if (filters.selectedCategory) {
    queryParams.append("category", filters.selectedCategory);
  }
  if (filters.searchValue) {
    queryParams.append("search", filters.searchValue);
  }
  queryParams.append("isVerified", "1");
  queryParams.append("status", "current");
};

export const buildEmployeeQuery = (
  filters: QueryFilters,
  paging: PagedQueryOptions,
) => {
  const queryParams = new URLSearchParams();
  appendCommonEmployeeParams(queryParams, filters);
  queryParams.append("pageNumber", String(paging.currentPage));
  queryParams.append("pageSize", String(paging.currentLimit));
  return queryParams.toString();
};

export const buildAttendanceQuery = (
  filters: AttendanceQueryFilters,
  paging: PagedQueryOptions,
) => {
  const queryParams = new URLSearchParams();
  appendCommonEmployeeParams(queryParams, filters);

  if (filters.attendanceStartDate) {
    queryParams.append("startDate", filters.attendanceStartDate);
  }
  if (filters.attendanceEndDate) {
    queryParams.append("endDate", filters.attendanceEndDate);
  }

  queryParams.append("pageNumber", String(paging.currentPage));
  queryParams.append("pageSize", String(paging.currentLimit));
  queryParams.append("type", "monthly");
  queryParams.append("isSalaryCreated", String(filters.isSalaryCreated ?? "1"));
  return queryParams.toString();
};

export const getPageInfoUpdate = (
  pageInfo: Partial<IPageInfo> | undefined,
  fallbackTotal: number,
  currentLimit: number,
) => ({
  total: pageInfo?.total || fallbackTotal || 0,
  totalPages:
    pageInfo?.totalPages ||
    Math.ceil((pageInfo?.total || fallbackTotal) / currentLimit) ||
    1,
});

export const resetPage = (pageInfo: IPageInfo): IPageInfo => ({
  ...pageInfo,
  currentPage: 1,
});

export const getSelectedUserIds = (selectedEmployeeIds: Set<string | number>) =>
  Array.from(selectedEmployeeIds).map(String);

export const hasPendingAttendanceRequests = (
  attendanceList: IAttendanceSummaryUser[],
) =>
  attendanceList.some((item) => {
    const summary = item.summary;
    if (!summary) return false;

    return (
      (summary.pending || 0) > 0
      // (summary.pendingAr || 0) > 0 ||
      // (summary.pendingLeave || 0) > 0 ||
      // (summary.pendingOutdoorDuty || 0) > 0 ||
      // (summary.pendingWfh || 0) > 0
    );
  });

export const getAttendanceLockState = (
  usersList: IAttendanceSummaryUser[],
  attendanceLock?: { isLocked?: boolean },
) => {
  const allUsersLocked =
    usersList.length > 0 &&
    usersList.every((item) => Boolean(item.attendanceLock?.isLocked));

  return Boolean(attendanceLock?.isLocked || allUsersLocked);
};

export const mapEmployeesToSalaryConfig = (
  employees: IUser[],
): ISalaryUserBackup[] =>
  employees.map((employee) => ({
    id: employee.id,
    name: employee.name,
    employeeCode: employee.employeeCode || "N/A",
    salaries: [],
  }));

export const getSalaryPreviewList = (
  salarySubTab: SalarySubTab,
  users: ISalaryPreviewUser[],
  errors: ISalaryPreviewError[],
) => (salarySubTab === "errors" ? errors : users);

export const filterSalaryPreviewList = <
  T extends ISalaryPreviewUser | ISalaryPreviewError,
>(
  list: T[],
  searchValue: string,
) => {
  if (!searchValue) return list;

  const term = searchValue.toLowerCase();
  return list.filter((item) => {
    const user = item.user;
    return (
      (user?.name || "").toLowerCase().includes(term) ||
      (user?.employeeCode || "").toLowerCase().includes(term)
    );
  });
};

export const paginateList = <T>(list: T[], pageInfo: IPageInfo) => {
  const page = pageInfo.currentPage || 1;
  const limit = pageInfo.pageSize || 10;
  const start = (page - 1) * limit;
  return list.slice(start, start + limit);
};

export const buildCreateMonthlySalaryPayload = (
  salaryPreviewUsers: ISalaryPreviewUser[],
  selectedSiteId: string,
  attendanceStartDate: string,
  attendanceEndDate: string,
): ICreateUserMonthlySalaryPayload => {
  const userIds = salaryPreviewUsers
    .map((salaryUser) => String(salaryUser.user?.id || ""))
    .filter(Boolean);

  const users = salaryPreviewUsers
    .map((salaryUser) => ({
      userId: String(salaryUser.user?.id || ""),
      userSalary: (salaryUser.userSalary || [])
        .map((salaryHead) => ({
          branchSalaryHeadId: String(
            salaryHead.branchSalaryHeadId || salaryHead.id || "",
          ),
          amount: Number(salaryHead.amount) || 0,
        }))
        .filter((item) => item.branchSalaryHeadId !== ""),
    }))
    .filter((item) => item.userId !== "");

  return {
    userIds,
    siteId: String(selectedSiteId),
    startDate: attendanceStartDate,
    endDate: attendanceEndDate,
    users,
  };
};
// f6a33dac-5ada-4e16-b682-19a015a8f832 net
// 133dd7fa-dd3c-42de-9532-99d4cc0b7ae4 gross
